import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, GitMerge, Play, RefreshCw, Trash2 } from 'lucide-react';
import type { IssueDto, ProjectAutonomy } from '@aiai/shared';
import { AutoMergeStatus, PullRequestState } from '@aiai/shared';
import { deleteIssue, mergeRunPullRequest, runIssue } from '../../api';
import { qk } from '../../api/queryKeys';
import { errorMessage } from '../../lib/api';
import { formatRelative } from '../../lib/format';
import { hasActiveRun } from '../../lib/issueRuns';
import { shortPersonLabel, useViewerEmail } from '../../lib/people';
import { useWorkspacePath } from '../../hooks/useWorkspacePath';
import { Spinner } from '../Spinner';
import { Row } from '../Row';
import { StatusBadge } from '../StatusBadge';
import { AssigneeChip } from '../AssigneeChip';
import { OverflowMenu, type OverflowMenuItem } from '../OverflowMenu';
import { ConfirmDialog } from '../ConfirmDialog';

export function IssueRow({
  issue,
  selected = false,
  onSelect,
  autonomy,
}: {
  issue: IssueDto;
  selected?: boolean;
  onSelect?: (id: string) => void;
  /** Project autonomy dial — drives the assignee chip for `open` and `proposed` issues. */
  autonomy?: ProjectAutonomy;
}) {
  const navigate = useNavigate();
  const { wp } = useWorkspacePath();
  const queryClient = useQueryClient();
  const viewerEmail = useViewerEmail();
  const [confirm, setConfirm] = useState(false);
  const [confirmMerge, setConfirmMerge] = useState(false);

  const run = useMutation({
    mutationFn: () => runIssue(issue.code ?? issue.id),
    onSuccess: (newRun) => {
      queryClient.invalidateQueries({ queryKey: qk.projectIssues(issue.projectId) });
      navigate(wp(`/runs/${newRun.id}`));
    },
  });

  const del = useMutation({
    mutationFn: () => deleteIssue(issue.code ?? issue.id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: qk.projectIssues(issue.projectId),
      });
      setConfirm(false);
    },
  });

  const isMergeReady =
    issue.latestRun?.pullRequestState === PullRequestState.Open &&
    issue.latestRun?.autoMergeStatus === AutoMergeStatus.AwaitingReview;

  const merge = useMutation({
    mutationFn: () => mergeRunPullRequest(issue.latestRun!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.projectIssues(issue.projectId) });
      setConfirmMerge(false);
      merge.reset();
    },
  });

  const isSelectable = onSelect !== undefined;
  const isActive = hasActiveRun(issue);

  const canRun = issue.state === 'open' || issue.state === 'failed';
  const prUrl = issue.latestRun?.pullRequestUrl ?? null;

  // Secondary actions live behind the overflow menu so the resting row stays
  // to just the title, description and state badge.
  const overflowItems: OverflowMenuItem[] = [];
  if (canRun) {
    overflowItems.push({
      label: 'Run',
      icon: run.isPending ? (
        <Spinner className="h-3.5 w-3.5" />
      ) : (
        <Play className="h-3.5 w-3.5" aria-hidden="true" />
      ),
      disabled: run.isPending,
      onClick: () => run.mutate(),
    });
  }
  if (prUrl) {
    overflowItems.push({
      label: 'View pull request',
      icon: <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />,
      onClick: () => window.open(prUrl, '_blank', 'noopener,noreferrer'),
    });
  }
  overflowItems.push({
    label: 'Delete',
    icon: <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />,
    danger: true,
    onClick: () => setConfirm(true),
  });

  // ConfirmDialog is a sibling of the Row, NOT inside its `actions` slot, so
  // the dialog's lifecycle is independent of the actions cell's layout.
  return (
    <>
      <Row
        to={wp(`/issues/${issue.code ?? issue.id}`)}
        status={
          isSelectable ? (
            /* relative z-10 lifts the checkbox above the stretched-link overlay */
            <span
              className="relative z-10 mt-0.5 flex-none"
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
            >
              <input
                type="checkbox"
                className="h-4 w-4 cursor-pointer rounded border-line accent-signal disabled:cursor-not-allowed disabled:opacity-40"
                checked={selected}
                onChange={() => onSelect!(issue.code ?? issue.id)}
                disabled={isActive}
                aria-label={`Select ${issue.title}`}
              />
            </span>
          ) : undefined
        }
        badge={
          <span className="flex items-center gap-2">
            <StatusBadge status={issue.state} size="sm" />
            <AssigneeChip issue={issue} autonomy={autonomy} />
          </span>
        }
        title={
          issue.code ? (
            <>
              <span className="mr-1.5 font-mono text-[0.7rem] uppercase tracking-wide text-ink-faint">
                {issue.code}
              </span>
              {issue.title}
            </>
          ) : (
            issue.title
          )
        }
        description={
          issue.createdByEmail ? (
            <span title={`Created by ${issue.createdByEmail}`}>
              by {shortPersonLabel(issue.createdByEmail, viewerEmail)}
            </span>
          ) : undefined
        }
        timestamp={formatRelative(issue.updatedAt)}
        actions={
          <>
            {isMergeReady && (
              <button
                type="button"
                className={merge.isError ? 'btn-danger btn-sm' : 'btn-primary btn-sm'}
                disabled={merge.isPending}
                onClick={(e) => { e.stopPropagation(); setConfirmMerge(true); }}
                aria-label={merge.isError ? 'Retry merge' : 'Merge PR'}
                title={
                  merge.isError
                    ? 'Merge failed — click to retry'
                    : (issue.latestRun?.autoMergeReason ?? 'PR is open and awaiting your review to merge')
                }
              >
                {merge.isPending ? (
                  <Spinner className="h-3.5 w-3.5" />
                ) : merge.isError ? (
                  <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
                ) : (
                  <GitMerge className="h-3.5 w-3.5" aria-hidden="true" />
                )}
                {merge.isError ? 'Retry merge' : 'Merge'}
              </button>
            )}
            <OverflowMenu items={overflowItems} size="sm" label={`More actions for ${issue.title}`} />
          </>
        }
      />

      <ConfirmDialog
        open={confirmMerge}
        title="Merge pull request?"
        confirmLabel="Merge pull request"
        busy={merge.isPending}
        error={
          merge.isError ? errorMessage(merge.error, 'Could not merge pull request') : null
        }
        onConfirm={() => merge.mutate()}
        onClose={() => { setConfirmMerge(false); merge.reset(); }}
        message={
          <>
            Merge the pull request for{' '}
            <span className="font-medium text-ink">{issue.title}</span>? This cannot be undone.
          </>
        }
      />

      <ConfirmDialog
        open={confirm}
        title="Delete issue?"
        confirmLabel="Delete issue"
        busy={del.isPending}
        error={
          del.isError ? errorMessage(del.error, 'Could not delete issue') : null
        }
        onConfirm={() => del.mutate()}
        onClose={() => setConfirm(false)}
        message={
          <>
            Delete{' '}
            <span className="font-medium text-ink">{issue.title}</span>{' '}
            and its run history? This cannot be undone.
          </>
        }
      />
    </>
  );
}
